import { useState, useEffect, useCallback, useMemo } from 'react';
import { DocumentTabSettings, UISettings } from '../../libs/ui-settings-manager/interfaces';
import { DEFAULT_UI_SETTINGS, UISettingsManager } from '../../libs/ui-settings-manager/UISettingsManager';

export const useDocumentTabSettings = (documentKey: string | undefined) => {
    const validDocumentKeys: Array<keyof UISettings['documents']> = [
        'invoiceItemCreator'       
    ];
    
    const getSafeDocumentKey = (key: string | undefined): keyof UISettings['documents'] => {
        if (key && validDocumentKeys.includes(key as keyof UISettings['documents'])) {
            return key as keyof UISettings['documents'];
        }
        if (key) {
            console.warn(`Клиюч документа: "${key}" не верный. Используется по дефолту "invoiceItemCreator"`);
        }
        return 'invoiceItemCreator';
    };
    
    const safeKey = getSafeDocumentKey(documentKey);

    // Получаем дефолтные настройки для документа
    const defaultSettings = useMemo(() => {
        return DEFAULT_UI_SETTINGS.documents[safeKey];
    }, [safeKey]);

    const getInitialSettings = (): DocumentTabSettings => {
        const allSettings = UISettingsManager.getSettings();
        const docSettings = allSettings.documents[safeKey];
        
        // Если настроек нет, возвращаем дефолтные
        if (!docSettings) {
            return defaultSettings;
        }
        
        return docSettings;
    };

    const [settings, setSettings] = useState<DocumentTabSettings>(getInitialSettings);

    // Подписываемся на изменения настроек
    useEffect(() => {
        const handleSettingsChange = (event: CustomEvent) => {
            const newSettings = event.detail as UISettings;
            const docSettings = newSettings.documents[safeKey];
            
            if (docSettings) {
                setSettings(docSettings);
            } else {
                // Если настроек нет, загружаем дефолтные
                setSettings(defaultSettings);
            }
        };

        window.addEventListener('uiSettingsChanged', handleSettingsChange as EventListener);
        return () => {
            window.removeEventListener('uiSettingsChanged', handleSettingsChange as EventListener);
        };
    }, [safeKey, defaultSettings]);

    // Обновление настроек для конкретного документа
    const updateSettings = useCallback((updates: Partial<DocumentTabSettings>) => {
        const currentSettings = UISettingsManager.getSettings();
        const currentDocSettings = currentSettings.documents[safeKey];
        
        // Создаем новые настройки для документа
        const newDocSettings: DocumentTabSettings = {
            ...currentDocSettings,
            ...updates
        };
        
        // Обновляем все настройки
        const newSettings = UISettingsManager.updateSection('documents', {
            [safeKey]: newDocSettings
        });
        
        setSettings(newSettings.documents[safeKey]);
    }, [safeKey]);

    // Сброс настроек документа к умолчаниям
    const resetToDefault = useCallback(() => {
        updateSettings(defaultSettings);
    }, [updateSettings, defaultSettings]);

    // Проверка, доступно ли поле для таба
    const isFieldEnabled = useCallback((fieldId: string): boolean => {
        return settings.enabledFields.includes(fieldId);
    }, [settings.enabledFields]);

    // Получение tabIndex для поля
    const getTabIndex = useCallback((fieldId: string): number => {
        return isFieldEnabled(fieldId) ? 0 : -1;
    }, [isFieldEnabled]);

    // Получение порядка табуляции
    const getTabOrder = useCallback((): string[] => {
        return settings.tabOrder;
    }, [settings.tabOrder]);

    const getBooleanSetting = useCallback((name: string): boolean => {
        if (!settings.documentSettings?.booleanSettings) {
            return true; // или другое значение по умолчанию
        }
        
        const setting = settings.documentSettings.booleanSettings.find(
            s => s.name === name
        );
        
        return setting?.state ?? true; // возвращаем значение настройки или true по умолчанию
    }, [settings.documentSettings?.booleanSettings]);

    const getStoreUISettings = useCallback((fieldName: string): string[] => {
        const storeValue = settings.documentStore?.find(s => s.fieldName === fieldName)?.value
        return storeValue || []
    }, [settings.documentStore])

    return {
        settings,
        defaultSettings,
        updateSettings,
        resetToDefault,
        isFieldEnabled,
        getTabIndex,
        getTabOrder,
        getBooleanSetting,
        getStoreUISettings,
        documentKey: safeKey,
    };
};